import _ from 'lodash'
import moment from 'moment'
import { HM_RECOMMENDATION_MAP } from '@/constant/questionnaire'
import { getI18nPropertyValue } from '@/i18n'

export default {
  methods: {
    formatNullableScore (score) {
      if ([null, undefined, ''].includes(score)) {
        return null
      }
      const formattedScore = _.round(parseFloat(score))
      return Number.isFinite(formattedScore) ? formattedScore : null
    },
    groupQuestionnaireResults (result, overallScoreMap) {
      const groupedResultList = _.groupBy(result.result_list, record => record.uuid)
      return _.map(groupedResultList, (records, uuid) => {
        const overallSummary = this.buildQuestionnaireResultsOverallSummary(records, overallScoreMap, uuid)
        return {
          ...result,
          uuid,
          overall_total_score: overallSummary.score,
          overall_score_updated_at: overallSummary.updatedAt,
          result_list: records
        }
      })
    },
    buildQuestionnaireResultsOverallSummary (records, overallScoreMap, uuid) {
      const score = _.get(overallScoreMap, [uuid])
      const scoredRecords = _.filter(records, record => record.status === 'submitted' && Number.isFinite(Number(record.total_score)))
      if (!scoredRecords.length) {
        return {
          score,
          updatedAt: null
        }
      }
      const updatedRecords = _.filter(scoredRecords, record => record.updated_time)
      const latestUpdatedRecord = _.maxBy(updatedRecords, record => moment(record.updated_time).valueOf())
      return {
        score,
        updatedAt: latestUpdatedRecord && latestUpdatedRecord.updated_time
      }
    },
    normalizeQuestionnaireResultsEvaluation (result) {
      const updatedAt = result.overall_score_updated_at
      return {
        updated_time: updatedAt,
        hr_score: this.formatNullableScore(result.overall_total_score),
        hr_score_total: this.getQuestionnaireResultsScoreTotal(result.result_list),
        auto_saved_time: updatedAt ? moment(updatedAt).format('HH:mm') : null,
        has_questionnaire: _.some(result.result_list, record => Boolean(record.questionnaire_id)),
        hm_scores: _.map(result.result_list, record => this.normalizeQuestionnaireResultRecord(record))
      }
    },
    getQuestionnaireResultsScoreTotal (records) {
      const recordWithScoreScale = _.find(records, record => record.score_scale)
      return recordWithScoreScale ? Number(recordWithScoreScale.score_scale.max_score) : null
    },
    normalizeQuestionnaireResultRecord (record) {
      const scoreScale = record.score_scale
      return {
        id: record.legacy_record_id,
        name: record.hm_name,
        completion: record.completion_rate,
        decision: record.decision,
        score: this.formatNullableScore(record.total_score),
        score_total: scoreScale ? Number(scoreScale.max_score) : null,
        recommend: HM_RECOMMENDATION_MAP[record.recommendation],
        comment: record.comment,
        has_questionnaire: Boolean(record.questionnaire_id),
        has_rating_question: _.some(record.items, item => item.question_type === 'rating'),
        questions: record.source_type === 'new_questionnaire'
          ? _.map(record.items, (item, index) => this.normalizeQuestionnaireResultItem(item, index, scoreScale))
          : []
      }
    },
    normalizeQuestionnaireResultItem (item, index, scoreScale) {
      const answer = item.answer || {}
      const scaleValues = scoreScale ? scoreScale.values : []
      const selectedScale = _.find(scaleValues, scale => String(scale.score_value) === String(item.score_value))
      return {
        id: item.questionnaire_item_id,
        number: index + 1,
        type: item.question_type,
        title: getI18nPropertyValue(item.question, 'title_zh', 'title_en'),
        required: item.is_required,
        weight: item.score_weight,
        score: this.formatNullableScore(item.score_value),
        scale_text: getI18nPropertyValue(selectedScale, 'label_zh', 'label_en') || '',
        scale_options: _.map(scaleValues, scale => ({
          value: scale.score_value,
          label: getI18nPropertyValue(scale, 'label_zh', 'label_en'),
          description: getI18nPropertyValue(scale, 'description_zh', 'description_en')
        })),
        comment: item.comment,
        answer: this.getHmQuestionAnswerText(item, answer),
        answer_options: this.getHmQuestionAnswerOptions(item, answer),
        submitted_time: item.submitted_at || item.saved_at
      }
    },
    getHmQuestionAnswerText (item, answer) {
      if (item.question_type === 'date') {
        return answer.date_value || '-'
      }
      if (answer.text_value) {
        return answer.text_value
      }
      const optionLabels = this.getHmQuestionAnswerOptions(item, answer)
      return optionLabels.length ? optionLabels.join(', ') : '-'
    },
    getHmQuestionAnswerOptions (item, answer) {
      const optionValues = answer.option_values || []
      return _.compact(_.map(optionValues, value => {
        const matchedOption = _.find(item.question.options, option => String(option.id) === String(value))
        return getI18nPropertyValue(matchedOption, 'label_zh', 'label_en')
      }))
    }
  }
}
